import { isDevMode } from '../../../shared/utils/isDevMode';
import type { WindowId } from './types';

const restorers = new Map<WindowId, () => void>();
export function registerWindowRestore(id: WindowId, restore: () => void): () => void {
  restorers.set(id, restore);
  return () => { if (restorers.get(id) === restore) restorers.delete(id); };
}
export function restoreOpenWindow(id: WindowId): void { restorers.get(id)?.(); }

/**
 * windowBridge — imperative seam for non-React callers that can't use the
 * useWindow hook to open/close a managed window: the `/tracker` chat command
 * (ChatCommandProcessor) and the GameUIOverlay action dispatcher (DEV-BAR
 * buttons). WindowManagerProvider wires `.toggle` to the live context toggle on
 * mount; before that it's a no-op.
 *
 * This is a SANCTIONED cross-boundary primitive (its own module, exempted in
 * .dependency-cruiser.cjs's no-gameplay-ui-to-diagnostics `to` carve-out — like
 * CopyButton / useFps) so gameplay dispatchers can drive windows without
 * importing the window-manager context or going back through UIContext.
 *
 * Generalized from the kit's old `ui/debug/debugToolsBridge.ts` (Phase 1 of the
 * UI-framework arc); the `debugToolsBridge` name is retired — callers import
 * `windowBridge` directly.
 */
export const windowBridge: {
  toggle: (id: WindowId) => void;
  open: (id: WindowId) => void;
  close: (id: WindowId) => void;
} = {
  toggle: () => {},
  /** Idempotent display opening, without reversing an already open window. */
  open: () => {},
  /** Idempotent display closing. */
  close: () => {},
};

// Vite's build constant also protects browsers with no Node process global.
const isDevBuild = typeof __H2000_DEV_BUILD__ === 'undefined' || __H2000_DEV_BUILD__;
if (typeof window !== 'undefined' && isDevBuild && isDevMode()) {
  (window as Window & { windowBridge?: typeof windowBridge }).windowBridge = windowBridge;
}
